import { useState } from 'react';

function PersonaAdmin({ adminToken, tabName }) {
    // Form state
    const [name, setName] = useState('');
    const [publicFirstName, setPublicFirstName] = useState('');
    const [bio, setBio] = useState('');
    const [voiceGuidelines, setVoiceGuidelines] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Personas management state
    const [personas, setPersonas] = useState([]);
    const [showPersonasList, setShowPersonasList] = useState(false);
    const [loadingPersonas, setLoadingPersonas] = useState(false);

    // Edit mode state
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    const loadPersonas = async () => {
        setLoadingPersonas(true);
        try {
            const response = await fetch('/api/admin/personas', {
                headers: { 'Authorization': `Bearer ${adminToken}` },
            });
            if (!response.ok) {
                throw new Error('Failed to fetch personas');
            }
            const personasData = await response.json();
            setPersonas(personasData);
            setShowPersonasList(true);
            setError('');
        } catch (err) {
            setError('Failed to load personas: ' + err.message);
        } finally {
            setLoadingPersonas(false);
        }
    };

    const editPersona = (persona) => {
        setName(persona.name);
        setPublicFirstName(persona.public_first_name);
        setBio(persona.bio || '');
        setVoiceGuidelines(persona.voice_guidelines || '');
        setIsActive(persona.is_active);
        setEditMode(true);
        setEditId(persona.id);
        setError('');
        setSuccess('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const cancelEdit = () => {
        setEditMode(false);
        setEditId(null);
        setName('');
        setPublicFirstName('');
        setBio('');
        setVoiceGuidelines('');
        setIsActive(true);
        setError('');
        setSuccess('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name.trim() || !publicFirstName.trim()) {
            setError('Name and public first name are required');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const url = editMode ? `/api/admin/personas/${editId}` : '/api/admin/personas';
            const method = editMode ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${adminToken}`,
                },
                body: JSON.stringify({
                    name: name.trim(),
                    public_first_name: publicFirstName.trim(),
                    bio: bio.trim(),
                    voice_guidelines: voiceGuidelines.trim(),
                    is_active: isActive
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to ${editMode ? 'update' : 'create'} persona`);
            }

            setSuccess(editMode ? 'Persona updated successfully!' : 'Persona created successfully!');
            cancelEdit();

            if (showPersonasList) {
                loadPersonas();
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const deletePersona = async (persona) => {
        if (!window.confirm(`Are you sure you want to delete "${persona.name}"? Posts already written under this persona keep their content but will lose the byline.`)) {
            return;
        }

        try {
            const response = await fetch(`/api/admin/personas/${persona.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${adminToken}` },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete persona');
            }

            setSuccess('Persona deleted successfully!');
            if (showPersonasList) {
                loadPersonas();
            }
            if (editMode && editId === persona.id) {
                cancelEdit();
            }
        } catch (err) {
            setError('Failed to delete persona: ' + err.message);
        }
    };

    return (
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
            <h3>{tabName}</h3>
            <p style={{ color: '#666', fontSize: '14px', marginTop: '-10px' }}>
                A persona is a reusable "voice" a blog post can be written as - background/experience and tone
                guidance to draft from. Only the public first name is ever shown to visitors as a byline; the
                bio and voice guidelines below are internal drafting material.
            </p>

            <div style={{ marginBottom: '30px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
                <h4 style={{ margin: '0 0 15px 0' }}>Manage Personas</h4>

                {!showPersonasList ? (
                    <button
                        onClick={loadPersonas}
                        disabled={loadingPersonas}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loadingPersonas ? 'not-allowed' : 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        {loadingPersonas ? 'Loading...' : 'Show All Personas'}
                    </button>
                ) : (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <span style={{ fontWeight: 'bold' }}>Personas ({personas.length})</span>
                            <button
                                onClick={() => setShowPersonasList(false)}
                                style={{
                                    padding: '5px 15px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Hide
                            </button>
                        </div>

                        {personas.length === 0 ? (
                            <p style={{ color: '#666', margin: '20px 0' }}>No personas found.</p>
                        ) : (
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {personas.map(persona => (
                                    <div key={persona.id} style={{
                                        border: '1px solid #e0e0e0',
                                        padding: '15px',
                                        margin: '10px 0',
                                        borderRadius: '4px',
                                        backgroundColor: 'white'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                            <h5 style={{ margin: 0, fontSize: '16px' }}>{persona.name}</h5>
                                            <span style={{
                                                padding: '2px 8px',
                                                borderRadius: '10px',
                                                fontSize: '11px',
                                                fontWeight: 'bold',
                                                backgroundColor: persona.is_active ? '#d4edda' : '#e2e3e5',
                                                color: persona.is_active ? '#155724' : '#41464b'
                                            }}>
                                                {persona.is_active ? 'Active' : 'Retired'}
                                            </span>
                                        </div>
                                        <p style={{ color: '#666', margin: '0 0 10px 0', fontSize: '14px' }}>
                                            Public byline: "By {persona.public_first_name}"
                                        </p>
                                        {persona.bio && (
                                            <p style={{ color: '#666', margin: '0 0 10px 0', fontSize: '13px' }}>
                                                {persona.bio.length > 150 ? persona.bio.slice(0, 150) + '...' : persona.bio}
                                            </p>
                                        )}
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            <button
                                                onClick={() => editPersona(persona)}
                                                style={{
                                                    padding: '5px 15px',
                                                    backgroundColor: '#007bff',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => deletePersona(persona)}
                                                style={{
                                                    padding: '5px 15px',
                                                    backgroundColor: '#dc3545',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {editMode && (
                <div style={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    padding: '15px',
                    borderRadius: '4px',
                    marginBottom: '20px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <strong>✏️ Editing Mode:</strong> {name || 'Loading...'}
                        </div>
                        <button
                            onClick={cancelEdit}
                            style={{
                                padding: '8px 15px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Cancel Edit
                        </button>
                    </div>
                </div>
            )}

            {error && (
                <div style={{
                    color: 'red',
                    padding: '10px',
                    marginBottom: '20px',
                    border: '1px solid red',
                    borderRadius: '4px',
                    backgroundColor: '#ffe6e6'
                }}>
                    {error}
                </div>
            )}

            {success && (
                <div style={{
                    color: 'green',
                    padding: '10px',
                    marginBottom: '20px',
                    border: '1px solid green',
                    borderRadius: '4px',
                    backgroundColor: '#e6ffe6'
                }}>
                    {success}
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Name (internal):
                    </label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Sarah Chen"
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '16px',
                            boxSizing: 'border-box'
                        }}
                        disabled={loading}
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                        For your own reference in the admin panel only - never shown to visitors.
                    </small>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Public first name:
                    </label>
                    <input
                        type="text"
                        value={publicFirstName}
                        onChange={(e) => setPublicFirstName(e.target.value)}
                        placeholder="e.g. Sarah"
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '16px',
                            boxSizing: 'border-box'
                        }}
                        disabled={loading}
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                        This is the only thing ever shown publicly - as the byline "By {publicFirstName || '...'}" on posts written under this persona.
                    </small>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Background / real experience (internal drafting brief):
                    </label>
                    <textarea
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        placeholder="Real experience, background, and expertise this persona draws on - seeds AI drafts with authentic detail instead of generic claims."
                        rows={5}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '14px',
                            lineHeight: '1.5',
                            boxSizing: 'border-box'
                        }}
                        disabled={loading}
                    />
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Voice / tone guidelines (internal):
                    </label>
                    <textarea
                        value={voiceGuidelines}
                        onChange={(e) => setVoiceGuidelines(e.target.value)}
                        placeholder="e.g. Warm and conversational, first-person, short sentences, no corporate jargon."
                        rows={4}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '14px',
                            lineHeight: '1.5',
                            boxSizing: 'border-box'
                        }}
                        disabled={loading}
                    />
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                            disabled={loading}
                        />
                        Active
                    </label>
                    <small style={{ color: '#666', fontSize: '12px' }}>
                        Uncheck to retire this persona - it stays visible on posts already written under it, but won't show up as an option for new posts.
                    </small>
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    style={{
                        padding: '12px 24px',
                        backgroundColor: loading ? '#ccc' : (editMode ? '#28a745' : '#007bff'),
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: '16px',
                        fontWeight: 'bold'
                    }}
                >
                    {loading ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update Persona' : 'Create Persona')}
                </button>
            </form>
        </div>
    );
}

export default PersonaAdmin;
